import { CommonProp, Heading, Modal, Text } from '@acin/ui-core';
import React, { useRef, useState } from 'react';
import { useIntersectionObserver } from '../../../hooks';
import { useReviewObligations } from '../../ReviewObligationsProvider';
import { useCreateInternalRegulationObligationRuleMutation } from '../graphql/mutations/createInternalRegulationObligationRule.generated';
import { useDeleteInternalRegulationObligationRuleMutation } from '../graphql/mutations/deleteInternalRegulationObligationRule.generated';
import { useObligationRules } from '../hooks/useObligationRules';
import * as SC from './ObligationRules.styled';
import { RuleItem } from './RuleItem';

export interface ObligationRulesProps extends CommonProp {
  className?: string;
  selectedObligationId?: string;
}

export const ObligationRules: React.FC<ObligationRulesProps> = ({ className, selectedObligationId }) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { activeObligation } = useReviewObligations();
  const { rules, loading, pageInfo, handleFetchMore } = useObligationRules(activeObligation?.id);
  const [localRules, setLocalRules] = useState(rules);
  const [ruleInput, setRuleInput] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRuleForDelete, setSelectedRuleForDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleteRule, { loading: deleteLoading }] = useDeleteInternalRegulationObligationRuleMutation();
  const [createRule] = useCreateInternalRegulationObligationRuleMutation();

  React.useEffect(() => {
    setLocalRules(rules);
  }, [activeObligation?.id, rules?.length]);

  const handleDeleteRule = (ruleId: string) => {
    const rule = localRules.find((r) => r.id === ruleId);
    if (rule) {
      setSelectedRuleForDelete({ id: ruleId, name: rule.name ?? '' });
      setIsModalOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedRuleForDelete) return;
    const { errors } = await deleteRule({
      variables: {
        input: { id: selectedRuleForDelete.id },
      },
      errorPolicy: 'all',
    });
    if (errors?.length) return;
    setLocalRules((prev) => prev.filter((rule) => rule.id !== selectedRuleForDelete.id));
    setIsModalOpen(false);
    setSelectedRuleForDelete(null);
  };

  const handleCancelDelete = () => {
    setIsModalOpen(false);
    setSelectedRuleForDelete(null);
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && ruleInput.trim()) {
      e.preventDefault();
      if (!selectedObligationId) return;
      const { data } = await createRule({
        variables: {
          input: {
            name: ruleInput.trim(),
            obligationId: selectedObligationId,
          },
        },
        errorPolicy: 'all',
      });
      if (data?.createInternalRegulationObligationRule) {
        const newRule = {
          ...data.createInternalRegulationObligationRule,
          __typename: 'InternalRegulationObligationRule' as const,
        };
        setLocalRules((prev) => [...prev, newRule]);
      }
      setRuleInput('');
    }
  };

  useIntersectionObserver(sentinelRef, handleFetchMore);

  return (
    <SC.ObligationRules className={className}>
      <Heading size={2}>Rules</Heading>
      <SC.List>
        {localRules.map((rule) => (
          <RuleItem key={rule.id} id={rule.id} name={rule.name} onDelete={handleDeleteRule} />
        ))}
        {loading && localRules.length > 0 && <SC.LoadingIndicator />}
        {pageInfo?.hasNextPage && <SC.Sentinel ref={sentinelRef} />}
      </SC.List>
      <SC.InputSection>
        <SC.RuleTextarea
          placeholder="Enter a new rule and press Enter to add"
          value={ruleInput}
          onChange={(e) => setRuleInput(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </SC.InputSection>
      <Modal
        show={isModalOpen}
        title="Delete this Rule?"
        footerButtonPrimary={{ label: 'Confirm', onClick: handleConfirmDelete, disabled: deleteLoading }}
        footerButtonSecondary={{ label: 'Cancel', onClick: handleCancelDelete }}
      >
        <Text>{`Are you sure you want to delete the following rule: ${selectedRuleForDelete?.name}?`}</Text>
      </Modal>
    </SC.ObligationRules>
  );
};
